import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Card from '../ui/Card';
import StatusBadge from '../ui/StatusBadge';
import DashboardProbeCard from './DashboardProbeCard';
import type { LoggerWithProbes } from '../../hooks/useDashboardData';

interface LoggerGroupProps {
  loggerData: LoggerWithProbes;
  siteName: string;
}

export default function LoggerGroup({ loggerData, siteName }: LoggerGroupProps) {
  const { t } = useTranslation();
  const { logger, probes, status } = loggerData;

  const formatLastSeen = (lastSeen?: string) => {
    if (!lastSeen) return t('common.never');

    const date = new Date(lastSeen);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) {
      return `${diffMins} min`;
    } else if (diffHours < 24) {
      return `${diffHours}h`;
    } else {
      return `${diffDays}j`;
    }
  };

  return (
    <Card className="mb-4">
      {/* En-tête Logger */}
      <div className="mb-4 pb-4 border-b border-gray-200">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <Link
              to={`/loggers/${logger.id}`}
              className="text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors"
            >
              {logger.name}
            </Link>
            <p className="text-sm text-gray-500">{logger.serial_number}</p>
          </div>
          {status !== 'ok' && <StatusBadge status={status} />}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Batterie */}
          {logger.battery_level !== undefined && logger.battery_level !== null && (
            <div>
              <p className="text-xs text-gray-600 mb-1">
                <span className="font-medium">{t('logger.battery')}:</span> {logger.battery_level}%
              </p>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    logger.battery_level > 50
                      ? 'bg-green-500'
                      : logger.battery_level > 20
                      ? 'bg-yellow-500'
                      : 'bg-red-500'
                  }`}
                  style={{ width: `${logger.battery_level}%` }}
                />
              </div>
            </div>
          )}

          {/* Last Seen */}
          <div>
            <p className="text-xs text-gray-600">
              <span className="font-medium">{t('logger.lastSeen')}:</span>
            </p>
            <p className="text-sm text-gray-900">{formatLastSeen(logger.last_seen_at)}</p>
          </div>

          {/* Nombre de sondes */}
          <div>
            <p className="text-xs text-gray-600">
              <span className="font-medium">{t('logger.probes')}:</span>
            </p>
            <p className="text-sm text-gray-900">
              {probes.length} {probes.length > 1 ? t('logger.probesPlural') : t('logger.probesSingular')}
            </p>
          </div>
        </div>
      </div>

      {/* Grille de cartes de sondes */}
      {probes.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {probes.map((probeData) => (
            <DashboardProbeCard
              key={probeData.probe.id}
              probeData={probeData}
              siteName={siteName}
              loggerName={logger.name}
            />
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500 italic">{t('dashboard.noProbesConfigured')}</p>
      )}
    </Card>
  );
}
